import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  Eye,
  EyeOff,
  ArrowRight,
  TrendingUp,
  ArrowLeft,
  Key,
  Clock,
} from "lucide-react";
import { toast } from "react-toastify";
import authService from "../../components/services/AuthService";

const VerifyOTP = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [timeLeft, setTimeLeft] = useState(30);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const otpRefs = useRef([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = location.state;

  // Countdown timer
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Move to next input
    if (value && index < 3) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const validationSchema = Yup.object({
    otp: Yup.string()
      .required("OTP is required")
      .length(4, "OTP must be 4 digits"),
    newPassword: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        "Password must contain at least one uppercase letter, one lowercase letter, and one number"
      )
      .required("New password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
      .required("Please confirm your password"),
  });

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      const payload = {
        password: values.newPassword,
        affiliate_code: userData.affiliate_code,
        otp: values.otp,
      };
      const response = await authService.resetPassword(payload);
      if (response.data?.status_code === "000") {
        toast.success(response.data?.reason);
        navigate("/login");
      } else {
        toast.error(response.data?.reason);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.reason ||
          "Password reset failed. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const resendOTP = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        emailOrPhone: userData.email,
      };
      const response = await authService.resendOtp(payload);
      if (response.data?.status_code === "000") {
        toast.success(response.data?.reason);
        setTimeLeft(30);
      } else {
        toast.error(
          response.data?.reason || "Failed to resend OTP. Please try again."
        );
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.reason ||
          "Failed to resend OTP. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-screen w-full flex flex-col sm:flex-row bg-[#FEFEFE]">
      {/* Left Side */}
      <motion.div
        className="relative hidden w-full h-full sm:w-1/2 sm:flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#FF6C6C] via-[#FF8B8B] to-[#FF9B9B]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {/* Background animations */}
        <motion.div
          className="absolute w-[800px] h-[800px] bg-white/10 rounded-full blur-xl"
          initial={{ scale: 0, rotate: 0 }}
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
        />

        {/* Floating stats cards */}
        <motion.div
          className="absolute top-10 right-10 z-20 p-4 rounded-2xl shadow-lg backdrop-blur-md bg-white/30"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          whileHover={{ scale: 1.05 }}
        >
          <div className="flex gap-3 items-center">
            <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-white">
              Secure Verification
            </span>
          </div>
        </motion.div>

        <motion.div
          className="absolute left-10 bottom-20 z-20 p-4 rounded-2xl shadow-lg backdrop-blur-md bg-white/30"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
          whileHover={{ scale: 1.05 }}
        >
          <div className="flex gap-3 items-center">
            <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-white">
              Instant Reset
            </span>
          </div>
        </motion.div>

        {/* Main image container */}
        <motion.div
          className="relative z-10 w-4/5 h-4/5"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <motion.div
            className="overflow-hidden relative w-full h-full rounded-2xl shadow-2xl backdrop-blur-sm bg-white/10"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <motion.img
              src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=600&fit=crop&crop=center"
              alt="dealboxx-otp-verification"
              className="object-cover w-full h-full rounded-2xl transition-transform duration-700 hover:scale-110"
              initial={{ scale: 1.1 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6 }}
            />

            {/* Content overlay */}
            <motion.div
              className="flex absolute inset-0 flex-col justify-end items-center pb-20 bg-gradient-to-b from-black/10 via-black/20 to-black/60"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              <motion.div
                className="px-8 text-center"
                initial={{ y: 20 }}
                animate={{ y: 0 }}
              >
                <motion.h2
                  className="mb-6 text-5xl font-bold text-white drop-shadow-lg"
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ delay: 1.2 }}
                >
                  Verify OTP
                </motion.h2>
                <motion.p
                  className="mx-auto mb-8 max-w-md text-xl text-white/90"
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ delay: 1.4 }}
                >
                  Enter the 4-digit code sent to your device
                </motion.p>
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Right Side */}
      <motion.div
        className="overflow-y-auto px-8 pt-16 pb-8 w-full sm:w-1/2 sm:h-screen sm:px-16"
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Logo */}
        <motion.div
          className="mb-6"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          <div className="flex gap-3 items-center">
            <div className="w-10 h-10 bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] bg-clip-text text-transparent">
              Dealboxx
            </span>
          </div>
        </motion.div>

        {/* Welcome Text */}
        <motion.div
          className="mb-6 space-y-2"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          <h1 className="text-3xl font-bold bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] bg-clip-text text-transparent">
            Verify & Reset
          </h1>
          <p className="text-[#808080] text-base">
            Enter the OTP code and create your new password
          </p>
        </motion.div>

        {/* Form */}
        <Formik
          initialValues={{
            otp: "",
            newPassword: "",
            confirmPassword: "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, setFieldValue }) => (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
            >
              <Form className="space-y-6">
                {/* OTP Input */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Enter OTP Code
                  </label>
                  <div className="flex gap-3 justify-center">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (otpRefs.current[index] = el)}
                        type="text"
                        maxLength="1"
                        value={digit}
                        onChange={(e) => {
                          handleOtpChange(index, e.target.value);
                          setFieldValue(
                            "otp",
                            [
                              ...otp.slice(0, index),
                              e.target.value,
                              ...otp.slice(index + 1),
                            ].join("")
                          );
                        }}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        className="w-12 h-12 text-center text-lg font-semibold border-2 border-gray-200 rounded-xl focus:border-transparent focus:ring-2 focus:ring-[#FF6C6C] focus:ring-offset-2 transition-all duration-200"
                      />
                    ))}
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <div className="flex gap-2 items-center text-sm text-gray-500">
                      <Clock className="w-4 h-4" />
                      <span>{formatTime(timeLeft)}</span>
                    </div>
                    <button
                      type="button"
                      onClick={resendOTP}
                      disabled={timeLeft > 0}
                      className="text-sm text-[#FF6C6C] hover:text-[#ff8b8b] disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      Resend OTP
                    </button>
                  </div>
                </div>

                {/* New Password Field */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    New Password
                  </label>
                  <div className="relative">
                    <Field
                      name="newPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your new password"
                      className="px-4 py-4 pr-12 w-full placeholder-gray-400 text-gray-900 rounded-2xl border transition-all duration-300 bg-white border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF6C6C] focus:border-transparent hover:border-gray-300"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 text-gray-400 transition-colors transform -translate-y-1/2 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  <ErrorMessage
                    name="newPassword"
                    component="div"
                    className="mt-1 text-sm text-red-500"
                  />
                </div>

                {/* Confirm Password Field */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <Field
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm your new password"
                      className="px-4 py-4 pr-12 w-full placeholder-gray-400 text-gray-900 rounded-2xl border transition-all duration-300 bg-white border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF6C6C] focus:border-transparent hover:border-gray-300"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-3 top-1/2 text-gray-400 transition-colors transform -translate-y-1/2 hover:text-gray-600"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  <ErrorMessage
                    name="confirmPassword"
                    component="div"
                    className="mt-1 text-sm text-red-500"
                  />
                </div>

                {/* Submit Button */}
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex gap-2 justify-center items-center px-6 py-4 w-full font-semibold text-white bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] rounded-2xl shadow-lg transition-all duration-300 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 rounded-full border-2 animate-spin border-white/30 border-t-white" />
                      Resetting Password...
                    </>
                  ) : (
                    <>
                      Reset Password
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </motion.button>
              </Form>
            </motion.div>
          )}
        </Formik>

        {/* Security Features */}
        <motion.div
          className="grid grid-cols-3 gap-3 mt-8"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.3, duration: 0.8 }}
        >
          <div className="p-3 text-center bg-white rounded-xl shadow-sm transition-shadow duration-300 hover:shadow-md">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">🔐</div>
              <div className="text-xs text-gray-600">Secure OTP</div>
            </motion.div>
          </div>
          <div className="p-3 text-center bg-white rounded-xl shadow-sm transition-shadow duration-300 hover:shadow-md">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">⚡</div>
              <div className="text-xs text-gray-600">Quick Setup</div>
            </motion.div>
          </div>
          <div className="p-3 text-center bg-white rounded-xl shadow-sm transition-shadow duration-300 hover:shadow-md">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">🛡️</div>
              <div className="text-xs text-gray-600">Protected</div>
            </motion.div>
          </div>
        </motion.div>

        {/* Security Info Section */}
        <motion.div
          className="mt-6 p-4 rounded-2xl bg-gradient-to-r from-[#FF6C6C]/10 to-[#FF9B9B]/10 border border-[#FF6C6C]/20"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.8 }}
        >
          <div className="flex gap-2 items-center mb-2">
            <Key className="w-4 h-4 text-[#FF6C6C]" />
            <span className="text-sm font-semibold text-[#FF6C6C]">
              Password Requirements
            </span>
          </div>
          <div className="space-y-1 text-xs text-gray-600">
            <div>• Minimum 8 characters</div>
            <div>• At least one uppercase & lowercase letter</div>
            <div>• At least one number</div>
          </div>
        </motion.div>

        {/* Back to Login */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-8 text-center"
        >
          <Link
            to="/login"
            className="flex items-center justify-center gap-2 text-sm text-gray-500 transition-colors hover:text-[#FF6C6C]"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default VerifyOTP;
